import { useEffect, useState } from "react";
import { Button, FormControl, InputLabel, MenuItem, Select, makeStyles, InputAdornment } from "@material-ui/core";
import { CircularProgress } from "@material-ui/core";
import { TextField } from "@material-ui/core";
import DetailsDialog, { DetailsMode, DetailsProps } from "../DetailsDialog";
import NumberInputField from "../ui/NumberInputField";
import TextInputField from "../ui/TextInputField";
import KitElementsAccordion from "./KitElementsAccordion";
import KitPackaging from "./KitPackaging";
import useKitApi, { PackagingType } from "../../api/KitApi";
import useFormApi from "../../api/FormApi";
import useShapeApi from "../../api/ShapeApi";
import useKitInconsistencyDetector from "./KitInconsistencyDetector";
import { formatCurrency, roundCurrency } from "../../engine/PriceUtilities";
import KitActiveOrders from "./KitActiveOrders";

interface Props extends DetailsProps {
  readonly customerId: number;
  readonly priceListId: number;
}

interface Kit {
  name: string;
  packagingPrice: number;
  packagingType: string;
  weight?: number
}

const useStyles = makeStyles(theme => ({
  button: {
    marginTop: theme.spacing(1)
  },
  inputField: {
    marginBottom: theme.spacing(1)
  }
}));

const KitDetails = (props: Props) => {
  const classes = useStyles();
  const formApi = useFormApi();
  const shapeApi = useShapeApi();
  const kitApi = useKitApi();
  const kitInconsistencyDetector = useKitInconsistencyDetector();
  const [kitId, setKitId] = useState<number|null>(null);
  const [name, setName] = useState<string>();
  const [packagingPrice, setPackagingPrice] = useState<number>();
  const [packagingType, setPackagingType] = useState<PackagingType>();
  const [weight, setWeight] = useState<number>();
  const [kitElementPositionPrices, setKitElementPositionPrices] = useState<any[]|null>(null);
  const [kitTotalPrice, setKitTotalPrice] = useState<number|null>(null);
  const kitElementPositionCount = kitElementPositionPrices === null ? null : kitElementPositionPrices.length;
  const effectiveKitId = kitId || props.instanceId;
  const customerId = props.customerId;
  const priceListId = props.priceListId;

  const updateWeight = (weight: number) => {
    console.debug(`updateWeight: ${weight}`);
    setWeight(weight);
  };

  const calculateFormPositionPrice = async (kitForm: any) => {
    const form = await formApi.fetchForm(customerId, priceListId, kitForm.form.id);
    return form.price.effective * kitForm.quantity;
  };

  const calculateShapePositionPrice = async (kitShape: any) => {
    const shape = await shapeApi.fetchShape(customerId, priceListId, kitShape.shape.id);
    return shape.unitPrice.effective * kitShape.quantity;
  };

  const calculateKitElementPositionPrices = async (kitId: number) => {
    const kitElementPositionPrices: any[] = [];
    const kitForms = await kitApi.fetchKitForms(customerId, priceListId, kitId);
    for (const kitForm of kitForms) {
      const formElementPositionPrice = await calculateFormPositionPrice(kitForm);
      kitElementPositionPrices.push(formElementPositionPrice);
    }
    const kitShapes = await kitApi.fetchKitShapes(customerId, priceListId, kitId);
    for (const kitShape of kitShapes) {
      const shapeElementPositionPrice = await calculateShapePositionPrice(kitShape)
      kitElementPositionPrices.push(shapeElementPositionPrice);
    }
    return kitElementPositionPrices;
  };

  const requestCalculationAndResettingKitElementPositionPrices = (kitId: number) => {
    setKitElementPositionPrices(null);
    calculateKitElementPositionPrices(kitId).then(setKitElementPositionPrices);
  }

  const initializeFields = (data: any, initial: boolean) => {
    if (initial === true) {
      setName(data.name);
      setPackagingPrice(data.packagingPrice);
      setPackagingType(data.packagingType);
    }
    updateWeight(data.weight);
    if (effectiveKitId !== null) {
      requestCalculationAndResettingKitElementPositionPrices(effectiveKitId);
      if (data.packagingType === 'specification') {
        kitInconsistencyDetector.reportInconsistenciesBetweenKitSpecificationAndPackagingSpecification(
          customerId,
          effectiveKitId,
          reportAlertsOrLogToConsole);
      }
    }
  };

  const collectData = () => {
    console.debug(`### collectData / weight = ${weight}`);
    return (name && packagingType && packagingPrice !== undefined)
    ? { name, packagingType, packagingPrice, weight }
    : null;
  };

  const onKitSave = (data: any) => {
    console.debug(`KitDetails:onSave: ${JSON.stringify(data)}`);
    setKitId(data.id);
  };

  const handlePackagingTypeChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const packagingType = event.target.value as PackagingType;
    setPackagingType(packagingType);
  };

  const renderTextFieldProgress = (label: string) => (
    <TextField
      label={label}
      className={classes.inputField}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <CircularProgress size={25} />
          </InputAdornment>
        )}} />
  );

  const logAlertsToConsole = (messages: string|string[]) => console.info(messages);

  const reportAlertOrLogToConsole = props.reportAlert || logAlertsToConsole;

  const reportAlertsOrLogToConsole = props.reportAlerts || logAlertsToConsole;

  const renderFields = (initialData: Kit|undefined, handleSaveClicked: (onSave?: (data: any) => void) => void) => (
    <>
      <h2>Wprowadź dane kompletu</h2>
      <TextInputField
        label='Nazwa'
        autoFocus
        initialValue={initialData?.name}
        onValueChange={setName}
        />
      <FormControl>
        <InputLabel id="packagingtype-label">Sposób pakowania</InputLabel>
        <Select
          labelId="packagingtype-label"
          value={packagingType || null}
          onChange={handlePackagingTypeChange}>
            <MenuItem value='specification'>zgodnie ze specyfikacją</MenuItem>
            <MenuItem value='foil'>folia</MenuItem>
            <MenuItem value='string'>wiązanie sznurkiem</MenuItem>
            <MenuItem value='pallet'>paleta</MenuItem>
            <MenuItem value='stretch'>folia stretch</MenuItem>
            <MenuItem value='none'>brak</MenuItem>
        </Select>
      </FormControl>
      <NumberInputField
        label='Cena pakowania'
        initialValue={initialData?.packagingPrice}
        calculatedValue={packagingPrice}
        onValueChange={setPackagingPrice}
      />
      <NumberInputField
        label='Waga całkowita'
        initialValue={initialData?.weight}
        calculatedValue={weight}
        onValueChange={updateWeight}
      />
      { effectiveKitId && kitTotalPrice === null
        ? renderTextFieldProgress('Cena całkowita')
        : <NumberInputField
            label='Cena całkowita'
            initialValue={ kitTotalPrice || 0 }
            calculatedValue={ kitTotalPrice || 0 }
            formatNumber={ value => formatCurrency(value) || '0' }
            disabled
          /> }
      { effectiveKitId && kitElementPositionCount === null
        ? renderTextFieldProgress('Ilość pozycji')
        : <NumberInputField
            label='Ilość pozycji'
            initialValue={kitElementPositionCount || 0}
            calculatedValue={kitElementPositionCount || 0}
            disabled
          /> }
      { effectiveKitId && <KitActiveOrders kitId={ effectiveKitId } /> }
      { effectiveKitId &&
        <KitPackaging
          classes={ classes }
          customerId={ customerId }
          priceListId={ priceListId }
          kitId={ effectiveKitId }
          reportAlert={ reportAlertOrLogToConsole }
        />
      }
      { !effectiveKitId &&
        <Button
          color='primary'
          className={classes.button}
          onClick={() => handleSaveClicked(onKitSave)}
          variant='contained'
        >
          Zapisz i kontynuuj wprowadzanie elementów
        </Button>
      }
    </>);

  const calculateTotalPrice = (prices: number[]) => {
    const totalPrice = prices.reduce((previous, current) => previous + current, 0);
    return Number(totalPrice.toFixed(2));
  };

  useEffect(() => {
    if (effectiveKitId) {
      requestCalculationAndResettingKitElementPositionPrices(effectiveKitId);
    }
  }, [effectiveKitId]);

  useEffect(() => {
    const kitElementsTotalPriceWithoutPackaging
      = kitElementPositionPrices === null ? 0 : calculateTotalPrice(kitElementPositionPrices);
    const kitElementsTotalPrice = roundCurrency(kitElementsTotalPriceWithoutPackaging + (packagingPrice || 0));
    setKitTotalPrice(kitElementsTotalPrice);
  }, [kitElementPositionPrices, packagingPrice]);

  const renderBottomRegion = (fetchDetails: (initial: boolean) => void) => (
    <>
      { effectiveKitId && <>
        <KitElementsAccordion
          customerId={ customerId }
          priceListId={ priceListId }
          kitId={ effectiveKitId }
          elementName='form'
          title='Formatki'
          promptMessage='Wybierz formatki do kompletu'
          onElementsChanged={ () => fetchDetails(false) }
        />
        <KitElementsAccordion
          customerId={ customerId }
          priceListId={ priceListId }
          kitId={ effectiveKitId }
          elementName='shape'
          title='Wykroje'
          promptMessage='Wybierz wykroje do kompletu'
          onElementsChanged={() => fetchDetails(false)}
        />
      </>}
    </>);

  return (
    <DetailsDialog
      { ...props }
      instanceId={effectiveKitId}
      mode={props.instanceId === null ? DetailsMode.Create : DetailsMode.Update}
      resource={`customers/${customerId}/price-lists/${priceListId}/kits`}
      renderFields={renderFields}
      initializeFields={initializeFields}
      collectData={collectData}
      renderBottomRegion={renderBottomRegion}
    />
  );
};
export default KitDetails;